// EnhancedOrderDetails.tsx - Updated with Dynamic Routing
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Form, FormControl, FormGroup, FormLabel, Alert, Spinner, Modal } from "react-bootstrap";
import ProductImg from "../../../images/product-img.png"
import Profile from "../../../images/profile-icon.svg"
import Call from "../../../images/call-icon.svg"
import Email from "../../../images/mail-icon.svg"
import LocationIcon from "../../../images/location-icon.svg"
import IconHome from "../../../images/icon-home.png"
import PhoneIcon from "../../../images/phone-icon.svg";
import AddressIcon from "../../../images/address-icon.svg";
import MailIcon from "../../../images/email-icon.png";
import { formatPhoneNumber } from '../../../utils/phoneFormatter';

// Import order-specific icons
import IconCupCake from "../../../images/orders/icon_cup-cake.png"
import IconGiftType from "../../../images/orders/icon_gift-type.png"
import IconCalendarFixed from "../../../images/orders/icon_calendar-fixed.png"
import IconCalendarOutlook from "../../../images/orders/icon_calendar-outlook.png"
import IconComments from "../../../images/orders/icon_comments.png"
import IconEnvelope from "../../../images/orders/icon_envelope.png"
import IconCallOrders from "../../../images/orders/icon_call.png"
import IconLocation from "../../../images/orders/icon_location.png"
import IconTransaction from "../../../images/orders/icon_trasaction.png"
import IconCard from "../../../images/orders/icon_card.png"
import IconCurrency from "../../../images/orders/icon_curr_icon.png"
import IconDeliveryVan from "../../../images/orders/icon_delivery-van.png"
import { Button } from 'react-bootstrap';
import { ordersApi, UpdateVerificationRequest, UpdateStatusRequest } from "./OrdersApi.tsx";
import { toast } from "react-toastify";
import { FaArrowLeftLong, FaDownload } from "react-icons/fa6";
import Swal from 'sweetalert2';
import { useContentLoader } from "../../../hooks/useContentLoader.tsx";
import PageWrapper from "../../Layout/PageWrapper.tsx";
import ContentWrapper from "../../Layout/ContentWrapper.tsx";
import "./Orders.scss"

interface OrderDetails {
  _id: string;
  orderId: string;
  orderDate: string;
  userName: string;
  occasion: string;
  type: string;
  occasionType?: string;
  date: string;
  milestoneEstimateDate?: string;
  milestoneConfirmed?: boolean;
  deathConfirmed?: boolean;
  giftStatus: string;
  paymentStatus: string;
  additionalComments: string;
  title?: string;
  recipientSnapshot?: {
    name: string;
    email: string;
    phoneNumber: string;
    alternatePhoneNumber?: string;
    address1: string;
    city: string;
    state: string;
    zip: string;
  };
  legacyContactSnapshot?: {
    name: string;
    email: string;
    phoneNumber: string;
    address1: string;
    city: string;
    state: string;
    zip: string;
  };
  userSnapshot?: {
    name: string;
    email: string;
    phoneNumber: string;
    address1: string;
    city: string;
    state: string;
    zip: string;
    ssn?: string;
  };
  userDetails?: {
    customerId: string;
    fullName: string;
    email: string;
    phoneNumber: string;
    address: {
      address1: string;
      address2: string;
      city: string;
      state: string;
      zip: string;
    };
    profileUrl?: string;
    accountStatus?: string;
  };
  giftSection?: {
    type: string;
    monetary?: { title: string; amount: number; imageUrl?: string };
    giftCard?: { title: string; amount: number; selectedAmount?: number; imageUrl?: string };
    physicalItem?: { title: string; price: number; description: string; imageUrl: string };
    message?: { title: string; content: string; imageUrl?: string };
  };
  messageType?: string;
  deliverySchedule: string;
  recurringDetails?: {
    frequency: string;
    numberOfYears: number;
  };
  paymentDetails?: {
    transactionId: string;
    amountPaid: number;
    cardBrand: string;
    cardLast4: string;
    cardExpMonth: number;
    cardExpYear: number;
    paymentMethod?: string;
    amount?: number;
    paymentDate?: string;
  };
  messages?: Array<{
    title: string;
    content: string;
  }>;
}

const EnhancedOrderDetails = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { withContentLoader } = useContentLoader();
  
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [downloading, setDownloading] = useState<boolean>(false);

  // Handle audio download
  const handleDownloadAudio = async () => {
    if (!orderDetails) return;
    try {
      setDownloading(true);
      await ordersApi.downloadAudio(orderDetails._id);
      toast.success('Audio downloaded successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to download audio');
    } finally {
      setDownloading(false);
    }
  };

  // Format gift type for display
  const formatGiftType = (giftType: string) => {
    const typeMapping = {
      'PhysicalItem': 'Physical',
      'GiftCard': 'Coupon',
      'Monetary': 'Monetary',
      'Message': 'Message'
    };
    return typeMapping[giftType as keyof typeof typeMapping] || giftType;
  };
  
  // Modal states
  const [showVerificationModal, setShowVerificationModal] = useState<boolean>(false);
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
  const [newStatus, setNewStatus] = useState<string>('');
  
  // Progressive status update state
  const [statusSelected, setStatusSelected] = useState<boolean>(false);

  // Fetch order details
  useEffect(() => {
    if (!orderId) {
      setError('Order ID is required');
      setLoading(false);
      return;
    }

    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Use the content loader for API call
        const response = await withContentLoader(
          () => ordersApi.getSingleOrder(orderId),
          'Loading order details...'
        );
        
        if (response.status === 'Success' && response.data) {
          setOrderDetails(response.data);
        } else {
          setError(response.message || 'Failed to fetch order details');
        }
      } catch (error: any) {
        console.error('Error fetching order details:', error);
        setError(error.message || 'Failed to fetch order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  // Handle verification update
  const handleVerificationUpdate = async (verificationData: UpdateVerificationRequest) => {
    if (!orderDetails) return;

    try {
      setUpdating(true);
      
      const response = await ordersApi.updateGiftVerification(orderDetails._id, verificationData);
      
      if (response.status === 'Success') {
        toast.success('Verification status updated successfully');
        setOrderDetails(prev => prev ? { ...prev, ...verificationData } : null);
        setShowVerificationModal(false);
      } else {
        toast.error(response.message || 'Failed to update verification status');
      }
    } catch (error: any) {
      console.error('Error updating verification:', error);
      toast.error(error.message || 'Failed to update verification status');
    } finally {
      setUpdating(false);
    }
  };

  // Handle progressive status update with confirmation
  const handleProgressiveStatusUpdate = async (nextStatus: string) => {
    if (!orderDetails) return;

    const result = await Swal.fire({
      title: 'Update Order Status?',
      text: `Are you sure you want to update the status from "${orderDetails.giftStatus}" to "${nextStatus}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3957A0',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Update Status',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      setUpdating(true);
      
      const statusData: UpdateStatusRequest = {
        giftStatus: nextStatus as 'Upcoming' | 'Processing' | 'Shipped' | 'Delivered'
      };
      
      const response = await ordersApi.updateGiftStatus(orderDetails._id, statusData);
      
      if (response.status === 'Success') {
        toast.success(`Gift status updated to ${nextStatus} successfully`);
        setOrderDetails(prev => prev ? { ...prev, giftStatus: nextStatus } : null);
        
        Swal.fire({
          title: 'Success!',
          text: `Order status has been updated to ${nextStatus}`,
          icon: 'success',
          confirmButtonColor: '#3957A0'
        });
      } else {
        toast.error(response.message || 'Failed to update gift status');
        Swal.fire({
          title: 'Error!',
          text: response.message || 'Failed to update gift status',
          icon: 'error',
          confirmButtonColor: '#3957A0'
        });
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast.error(error.message || 'Failed to update gift status');
      
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Failed to update gift status',
        icon: 'error',
        confirmButtonColor: '#3957A0'
      });
    } finally {
      setUpdating(false);
    }
  };

  // Get next status in the flow
  const getNextStatus = (currentStatus: string) => {
    const statusFlow = {
      'Upcoming': 'Processing',
      'Pending': 'Processing',
      'Processing': 'Shipped', 
      'Shipped': 'Delivered'
    };
    return statusFlow[currentStatus as keyof typeof statusFlow];
  };

  // Handle status update (keep for modal if needed)
  const handleStatusUpdate = async () => {
    if (!orderDetails || !newStatus) return;

    try {
      setUpdating(true);
      
      const statusData: UpdateStatusRequest = {
        giftStatus: newStatus as 'Upcoming' | 'Processing' | 'Shipped' | 'Delivered'
      };
      
      const response = await ordersApi.updateGiftStatus(orderDetails._id, statusData);
      
      if (response.status === 'Success') {
        toast.success('Gift status updated successfully');
        setOrderDetails(prev => prev ? { ...prev, giftStatus: newStatus } : null);
        setShowStatusModal(false);
        setNewStatus('');
      } else {
        toast.error(response.message || 'Failed to update gift status');
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast.error(error.message || 'Failed to update gift status');
    } finally {
      setUpdating(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Capitalize text helper
  const capitalizeText = (text: string) => {
    if (!text) return 'N/A';
    // Capitalize first letter of each word
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Get initials from name (similar to Header component)
  const getInitials = (name: string) => {
    if (!name) return 'U'; // Default to 'U' for User if no name
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  const getStatusClass = (status: string | undefined | null): string => {
    return typeof status === "string" && status.toLowerCase() === "active"
      ? "tag active"
      : "tag inactive";
  };

  // Format date and time for display
  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }) + ' - ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch {
      return 'Invalid Date';
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Get verification status badge
  const getVerificationBadge = () => {
    if (!orderDetails) return null;

    if (orderDetails.type === 'Post-Mortem') {
      return (
        <span className={`badge ${orderDetails.deathConfirmed ? 'bg-success' : 'bg-warning text-dark'}`}>
          {orderDetails.deathConfirmed ? 'Death Verified' : 'Death Verification Pending'}
        </span>
      );
    }

    if (orderDetails.occasionType === 'Conditional') {
      return (
        <span className={`badge ${orderDetails.milestoneConfirmed ? 'bg-success' : 'bg-warning text-dark'}`}>
          {orderDetails.milestoneConfirmed ? 'Milestone Verified' : 'Milestone Verification Pending'}
        </span>
      );
    }

    return <span className="badge bg-info">Fixed Date</span>;
  };

  // Determine if order needs verification
  const needsVerification = () => {
    if (!orderDetails) return false;
    return (orderDetails.type === 'Post-Mortem' && !orderDetails.deathConfirmed) ||
           (orderDetails.occasionType === 'Conditional' && !orderDetails.milestoneConfirmed);
  };

  // Get gift display details
  const getGiftDetails = () => {
    if (!orderDetails) return null;

    if (orderDetails.giftSection) {
      const section = orderDetails.giftSection;
      return {
        image: section.physicalItem?.imageUrl || section.monetary?.imageUrl || section.giftCard?.imageUrl || section.message?.imageUrl || ProductImg,
        title: section.monetary?.title || section.giftCard?.title || section.physicalItem?.title || section.message?.title || 'Gift Item',
        description: section.physicalItem?.description || section.message?.content || `${formatGiftType(section.type)} gift for ${orderDetails.occasion}`,
        amount: section.monetary?.amount || section.giftCard?.selectedAmount || section.giftCard?.amount || section.physicalItem?.price || 0
      };
    }

    // Handle message-only gifts
    const messageTitle = orderDetails.messages && orderDetails.messages.length > 0 
      ? orderDetails.messages[0].title 
      : 'Personal Message';
    
    return {
      image: ProductImg,
      title: messageTitle,
      description: `A heartfelt personal message for ${orderDetails.occasion}`,
      amount: 0
    };
  };

  // Loading state
  if (loading) {
    return (
      <section className="main-body">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading order details...</p>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="main-body">
        <Alert variant="danger" className="m-4">
          <h5>Error Loading Order</h5>
          <p>{error}</p>
          <Button color="primary" onClick={() => navigate('/orders')}>
            Back to Orders
          </Button>
        </Alert>
      </section>
    );
  }

  // No data state
  if (!orderDetails) {
    return (
      <section className="main-body">
        <Alert variant="warning" className="m-4">
          <h5>Order Not Found</h5>
          <p>The requested order could not be found.</p>
          <Button color="primary" onClick={() => navigate('/orders')}>
            Back to Orders
          </Button>
        </Alert>
      </section>
    );
  }

  const giftDetails = getGiftDetails();

  return (
    <PageWrapper>
      <ContentWrapper>
        <section className="main-body">
        {/* Header Section */}
        <section className="order-header-section">
          <div className="container-fluid">
            <div className="row align-items-center mb-4">
              <div className="col-md-8">
                {/* <h1 className="order-title">Order ID: {orderDetails.orderId}</h1> */}
                <nav className="breadcrumb-nav">
                  <Link to="/orders" className="text-decoration-none " style={{color:'#3957A0'}}>
                    <span><img src={IconHome} alt="home" /> <span className='my-auto'>Orders</span></span>
                  </Link>
                  <span className="mx-2 text-muted">›</span>
                  <span className="text-dark">{orderDetails.orderId}</span>
                </nav>
              </div>
              <div className="col-md-4 text-end">
                <Button color="secondary" className='bg-transparent border-0 fw-bold' style={{color:'#3957A0'}} onClick={() => navigate('/orders')}>
                  <FaArrowLeftLong color='#3957A0'/> Back
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Profile Header */}
        {orderDetails.userDetails && (
          <div className='profile-area'>
            <div className='row justify-content-between' style={{ alignItems: "start" }}>
              <aside className='profile-block col-xl-3 col-lg-3 col-md-6 col-12'>
                <figure>
                  {orderDetails.userDetails.profileUrl ? (
                    <img
                      src={orderDetails.userDetails.profileUrl}
                      alt={orderDetails.userDetails.fullName || "Profile"}
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          const fallbackDiv = document.createElement('div');
                          fallbackDiv.className = 'profile-circle-grid rounded-circle  text-white d-flex justify-content-center align-items-center';
                          fallbackDiv.style.width = '100px';
                          fallbackDiv.style.height = '100px';
                          fallbackDiv.textContent = getInitials(orderDetails.userDetails?.fullName || '');
                          parent.appendChild(fallbackDiv);
                        }
                      }}
                    />
                  ) : (
                    <div className="profile-circle-grid rounded-circle  text-white d-flex justify-content-center align-items-center" style={{ width: '100px', height: '100px' }}>
                      {getInitials(orderDetails.userDetails.fullName)}
                    </div>
                  )}
                </figure>
                <div className="profile-cont">
                  <h3>{capitalizeText(orderDetails.userDetails.fullName) || "No name provided"}</h3>
                  <span className={getStatusClass(orderDetails.userDetails.accountStatus)}>
                    {capitalizeText(orderDetails.userDetails.accountStatus) || "Unknown"}
                  </span>
                </div>
              </aside>
              <aside className='profile-det col-xl-3 col-lg-3 col-md-6 col-12 mt-3'>
                <div className='profile-det-in'>
                  <div className='icon'>
                    <img src={MailIcon} alt='Email' />
                  </div>
                  <div className='profile-det-cont'>
                    <h3>Email</h3>
                    <p>{orderDetails.userDetails?.email || orderDetails.userSnapshot?.email || "Not provided"}</p>
                  </div>
                </div>
              </aside>
              <aside className='profile-det col-xl-3 col-lg-3 col-md-6 col-12 mt-3'>
                <div className='profile-det-in'>
                  <div className='icon'>
                    <img src={AddressIcon} alt='Phone' />
                  </div>
                  <div className='profile-det-cont'>
                    <h3>Phone</h3>
                    <p>{formatPhoneNumber(orderDetails.userDetails.phoneNumber) || "Not provided"}</p>
                  </div>
                </div>
              </aside>
              <aside className='profile-det col-xl-3 col-lg-3 col-md-6 col-12 mt-3'>
                <div className='profile-det-in'>
                  <div className='icon'>
                    <img src={AddressIcon} alt='Address' />
                  </div>
                  <div className='profile-det-cont'>
                    <h3>Address</h3>
                    <p>
                      {orderDetails.userDetails.address?.address1 || orderDetails.userDetails.address?.city || orderDetails.userDetails.address?.state || orderDetails.userDetails.address?.zip
                        ? [
                            orderDetails.userDetails.address?.address1,
                            orderDetails.userDetails.address?.city,
                            orderDetails.userDetails.address?.state,
                            orderDetails.userDetails.address?.zip
                          ].filter(Boolean).join(', ')
                        : 'Not provided'
                      }
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        )}

        {/* Combined Details Block */}
        <div className="details-block">
          <div className="details-block-in">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="section-title mb-0 fw-bold" style={{fontSize:'20px'}}>Gift Information</h2>
              <div className="d-flex gap-2 align-items-center">
                <span className={`badge ${
                  orderDetails.giftStatus === 'Delivered' ? 'bg-success' :
                  orderDetails.giftStatus === 'Shipped' ? 'bg-info' :
                  orderDetails.giftStatus === 'Processing' ? 'bg-warning text-dark' :
                  'bg-secondary'
                }`}>
                  {orderDetails.giftStatus || 'Pending'}
                </span>
                {orderDetails.messageType === 'Physical' && (
                  <Button
                    size="sm"
                    disabled={downloading}
                    onClick={handleDownloadAudio}
                    style={{
                      backgroundColor: '#3957A0',
                      borderColor: '#3957A0',
                      borderRadius: '8px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <FaDownload size={14} />
                    {downloading ? 'Downloading...' : 'Download Audio'}
                  </Button>
                )}
              </div>
            </div>
            
            <div className="row">
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconCupCake} alt="Gift Title" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Gift Title</label>
                       <p className="fw-bold mb-0">{capitalizeText(orderDetails.title || giftDetails?.title || 'Gift Item')}</p>
                    </div>
                  </div>
                 
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconGiftType} alt="Gift Type" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Gift Type</label>
                      <p className="fw-bold mb-0">{formatGiftType(orderDetails.giftSection?.type) || 'Regular Gift'}</p>
                    </div>
                  </div>
                  
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconCalendarFixed} alt="Set Occasion" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Set Occasion</label>
                      <p className="fw-bold mb-0">{capitalizeText(orderDetails.occasion)}</p>
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
            
            <div className="row">
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconDeliveryVan} alt="Recurring Schedule" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Delivery Schedule</label>
                      <p className="fw-bold mb-0">
                    {orderDetails.recurringDetails ? 
                      `${orderDetails.recurringDetails.frequency} (${orderDetails.recurringDetails.numberOfYears} Years)` : 
                      orderDetails.deliverySchedule}
                  </p>
                    </div>
                  </div>
                  
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconCalendarFixed} alt="Set Delivery Date" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Occasion Date</label>
                      <p className="fw-bold mb-0">{formatDate(orderDetails.date)}</p>
                    </div>
                  </div>
                  
                </div>
              </div>
              
              {/* <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconCalendarOutlook} alt="Recurring End Date" width="32"  />
                    </div>

                    <div>
                      <label className="form-label mb-0 text-muted">Recurring End Date</label>
                      <p className="fw-bold mb-0">{formatDate(orderDetails.milestoneEstimateDate || orderDetails.date)}</p>
                    </div>

                    
                  </div>
                  
                </div>
              </div> */}

              <div className="col-md-12">
<div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={IconComments} alt="Set Delivery Date" width="32"  />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Additional Comments</label>
                      <p className="fw-bold mb-0">{orderDetails.additionalComments || 'N/A'}</p>
                    </div>
                  </div>
                  
                </div>

                
              </div>
            </div>
            
            {/* Additional Comments */}
            {(orderDetails.messages && orderDetails.messages.length > 0) && (
              <div className="row">
                <div className="col-12">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <div className="gift-icon me-2">
                        <img src={IconComments} alt="Additional Comments" width="32"  />
                      </div>
                      <div>
                        <label className="form-label mb-0 text-muted">Additional Comments</label>
                        <p className="fw-bold mb-0">
                      {orderDetails.messages.map((message, index) => (
                        <span key={index}>
                          {message.content}
                          {index < orderDetails.messages.length - 1 ? '; ' : ''}
                        </span>
                      ))}
                    </p>
                      </div>
                    </div>
                    
                  </div>
                </div>
              </div>
            )}
          <hr/>
          {/* Recipient & Delivery Information */}
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="section-title mb-0 fw-bold" style={{fontSize:'20px'}}>Recipient & Delivery Information</h2>
            </div>
            
            <div className="row">
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Profile} alt="Name" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Name</label>
                      <p className="fw-bold mb-0">{capitalizeText(orderDetails.recipientSnapshot?.name || orderDetails.userName)}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Email} alt="Email" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Email</label>
                      <p className="fw-bold mb-0">{orderDetails.recipientSnapshot?.email || orderDetails.userSnapshot?.email}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Call} alt="Phone" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Phone</label>
                      <p className="fw-bold mb-0">{formatPhoneNumber(orderDetails.recipientSnapshot?.phoneNumber || orderDetails.userSnapshot?.phoneNumber)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={Call} alt="Alternate Phone" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Alternate Phone</label>
                      <p className="fw-bold mb-0">{orderDetails.recipientSnapshot?.alternatePhoneNumber || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-8">
                <div className="gift-info-item mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className="gift-icon me-2">
                      <img src={LocationIcon} alt="Address" width="32" className="gift-icon" />
                    </div>
                    <div>
                      <label className="form-label mb-0 text-muted">Address</label>
                      <p className="fw-bold mb-0">
                        {(orderDetails.recipientSnapshot?.address1 || orderDetails.userSnapshot?.address1)}<br />
                        {(orderDetails.recipientSnapshot?.city || orderDetails.userSnapshot?.city)}, {' '}
                        {(orderDetails.recipientSnapshot?.state || orderDetails.userSnapshot?.state)} {' '}
                        {(orderDetails.recipientSnapshot?.zip || orderDetails.userSnapshot?.zip)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        {orderDetails.paymentDetails?.amountPaid !== 0 && (
          <>
            <hr/>

            {/* Product & Payment Information Combined */}
            <div className="row">
              {/* Product Information */}
              <div className="col-md-5" >
            <div className="mt-0">
              <div className="" style={{backgroundColor:'#EFF4FF', padding:'20px', borderRadius:'8px'}}>
                <div className="product-payment-display">
                  <div className="row">
                    {/* Product Image and Details */}
                    <div className="col-md-4">
                      <div className="product-image-container">
                        {giftDetails && giftDetails.image && giftDetails.image !== ProductImg ? (
                          <img 
                            src={giftDetails.image} 
                            alt="Gift" 
                            className="img-fluid rounded product-image"
                            onError={(e) => {
                              const placeholder = document.createElement('div');
                              placeholder.className = 'img-fluid rounded product-image d-flex align-items-center justify-content-center bg-light text-muted';
                              placeholder.style.minHeight = '120px';
                              placeholder.style.fontSize = '14px';
                              placeholder.style.border = '2px dashed #dee2e6';
                              placeholder.style.flexDirection = 'column';
                              placeholder.innerHTML = `
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="mb-2">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                  <circle cx="8.5" cy="8.5" r="1.5"/>
                                  <polyline points="21,15 16,10 5,21"/>
                                </svg>
                                <span>No Image Available</span>
                              `;
                              e.currentTarget.parentNode?.replaceChild(placeholder, e.currentTarget);
                            }}
                          />
                        ) : (
                          <div 
                            className="img-fluid rounded product-image d-flex align-items-center justify-content-center bg-light text-muted"
                            style={{
                              minHeight: '120px',
                              fontSize: '14px',
                              border: '2px dashed #dee2e6',
                              flexDirection: 'column'
                            }}
                          >
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="mb-2">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                              <circle cx="8.5" cy="8.5" r="1.5"/>
                              <polyline points="21,15 16,10 5,21"/>
                            </svg>
                            <span>No Image Available</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="col-md-8">
                      <div className="product-details-section">
                        <h3 className="product-title" style={{color:'#382F2D'}}>{giftDetails?.title || 'Gift Item'}</h3>
                        <h2 className="product-price mb-2" style={{color:'#3957A0'}}>
                          {giftDetails?.amount ? formatCurrency(giftDetails.amount) : '$0.00'}
                        </h2>
                        <p className="product-description text-muted">
                          {giftDetails?.description || 'A thoughtful gift for a special occasion.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          
          {/* Payment Information */}
          <div className="col-md-7">


          <div className='row'>
            <h3 className="section-title mb-4 fw-bold" style={{fontSize:'20px'}}>Payment Information</h3>
            <div className='col-md-6'>
              <div className="info-item d-flex align-items-center mb-3">
                    <img src={IconTransaction} alt="" className="me-3" width="24" height="24" />
                    <div>
                      <label className="form-label mb-1 text-muted">Transaction ID</label>
                      <p className="fw-bold">{orderDetails.paymentDetails?.transactionId || 'N/A'}</p>
                    </div>
                  </div>
            </div>

            <div className='col-md-6'>
              <div className="info-item d-flex align-items-center mb-3">
                    <img src={IconCalendarOutlook} alt="" className="me-3" width="24" height="24" />
                    <div>
                      <label className="form-label mb-1 text-muted">Date & Time</label>
                      <p className="fw-bold">{formatDateTime(orderDetails.orderDate)}</p>
                    </div>
                  </div>
            </div>

            <div className='col-md-6'>
              <div className="info-item d-flex align-items-center mb-3">
                    <img src={IconCard} alt="" className="me-3" width="24" height="24" />
                    <div>
                      <label className="form-label mb-1 text-muted">Payment Method</label>
                      <p className="fw-bold">{orderDetails.paymentDetails?.cardBrand ? `${orderDetails.paymentDetails.cardBrand.toUpperCase()} **** ${orderDetails.paymentDetails.cardLast4}` : 'N/A'}</p>
                    </div>
                  </div>
            </div>

            <div className='col-md-6'>
              <div className="info-item d-flex align-items-center mb-3">
                    <img src={IconCurrency} alt="" className="me-3" width="24" height="24" />
                    <div>
                      <label className="form-label mb-1 text-muted">Paid Amount</label>
                      <p className={`fw-bold ${(orderDetails.paymentDetails?.amountPaid || orderDetails.paymentDetails?.amount) ? 'text-success fs-5' : ''}`}>
                      {orderDetails.paymentDetails?.amountPaid ? formatCurrency(orderDetails.paymentDetails.amountPaid) : (orderDetails.paymentDetails?.amount ? formatCurrency(orderDetails.paymentDetails.amount) : 'N/A')}
                    </p>
                    </div>
                  </div>
            </div>
          </div>



          </div>
        </div>
          </>
        )}

        <hr/>
        
        {/* Status Section - Progressive Checkbox */}
        <div className="status-section" style={{backgroundColor:'#EFF4FF', padding:'20px', borderRadius:'8px', marginTop:'20px'}}>
          <h3 className="section-title mb-3 fw-bold" style={{fontSize:'16px'}}>Status</h3>
            
            <div className="status-flow">
              {/* Current Status Display */}
              {/* <div className="current-status mb-3">
                <span className="text-muted me-2">Current Status:</span>
                <span className={`badge ${
                  orderDetails.giftStatus === 'Delivered' ? 'bg-success' :
                  orderDetails.giftStatus === 'Shipped' ? 'bg-info' :
                  orderDetails.giftStatus === 'Processing' ? 'bg-warning text-dark' :
                  'bg-secondary'
                }`}>
                  {orderDetails.giftStatus || 'Pending'}
                </span>
              </div> */}

              {/* Progressive Checkbox and Update Button - Only show if there's a next status */}
              {getNextStatus(orderDetails.giftStatus) && (
                <div className="d-flex align-items-center gap-3">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="progressiveStatus"
                      checked={statusSelected}
                      disabled={updating}
                      onChange={(e) => setStatusSelected(e.target.checked)}
                    />
                    <label className="form-check-label fw-bold" htmlFor="progressiveStatus">
                      {getNextStatus(orderDetails.giftStatus)}
                    </label>
                  </div>
                  
                  <Button 
                    color="primary" 
                    size="sm"
                    disabled={updating || !statusSelected}
                    onClick={() => {
                      const nextStatus = getNextStatus(orderDetails.giftStatus);
                      if (nextStatus && statusSelected) {
                        handleProgressiveStatusUpdate(nextStatus);
                        setStatusSelected(false); // Reset checkbox after update
                      }
                    }}
                  >
                    {updating ? 'Updating...' : 'Update'}
                  </Button>
                </div>
              )}

              {/* Status completed message */}
              {orderDetails.giftStatus === 'Delivered' && (
                <div className="alert alert-success mt-3">
                  <strong>Order Complete:</strong> This gift has been delivered successfully.
                </div>
              )}
            </div>
            
            {/* Verification section if needed */}
            {needsVerification() && (
              <div className="verification-section mt-3">
                <div className="d-flex align-items-center gap-3">
                  <div>
                    <span className="text-muted me-2">Verification Status:</span>
                    {getVerificationBadge()}
                  </div>
                  <Button 
                    color="warning" 
                    size="sm"
                    onClick={() => setShowVerificationModal(true)}
                    disabled={updating}
                  >
                    {updating ? 'Updating...' : 'Update Verification'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        </div>
        </section>
      </ContentWrapper>

      {/* Verification Modal */}
      <Modal 
        show={showVerificationModal} 
        onHide={() => setShowVerificationModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {orderDetails.type === 'Post-Mortem' ? 'Death Verification' : 'Milestone Verification'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {orderDetails.type === 'Post-Mortem' ? (
            <div>
              <p><strong>Order:</strong> {orderDetails.orderId}</p>
              <p><strong>Customer:</strong> {orderDetails.userName}</p>
              <p><strong>Occasion:</strong> {orderDetails.occasion}</p>
              <Alert variant="warning">
                <strong>Warning:</strong> Please ensure you have received and verified the death certificate 
                before confirming death verification. Once confirmed, this will trigger the gift delivery process.
              </Alert>
              <div className="d-flex gap-2 justify-content-end">
                <Button 
                  color="secondary" 
                  onClick={() => setShowVerificationModal(false)}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button 
                  color="success" 
                  onClick={() => handleVerificationUpdate({ deathConfirmed: true })}
                  disabled={updating}
                >
                  {updating ? 'Confirming...' : 'Confirm Death Verification'}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <p><strong>Order:</strong> {orderDetails.orderId}</p>
              <p><strong>Customer:</strong> {orderDetails.userName}</p>
              <p><strong>Milestone:</strong> {orderDetails.occasion}</p>
              <p><strong>Estimated Date:</strong> {formatDate(orderDetails.milestoneEstimateDate || orderDetails.date)}</p>
              <Alert variant="info">
                <strong>Milestone Verification:</strong> Please confirm that the milestone event has occurred 
                or provide documentation if required before confirming verification.
              </Alert>
              <div className="d-flex gap-2 justify-content-end">
                <Button 
                  color="secondary" 
                  onClick={() => setShowVerificationModal(false)}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button 
                  color="success" 
                  onClick={() => handleVerificationUpdate({ milestoneConfirmed: true })}
                  disabled={updating}
                >
                  {updating ? 'Confirming...' : 'Confirm Milestone Verification'}
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Status Update Modal */}
      <Modal 
        show={showStatusModal} 
        onHide={() => setShowStatusModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Gift Status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <p><strong>Order:</strong> {orderDetails.orderId}</p>
            <p><strong>Current Status:</strong> {orderDetails.giftStatus}</p>
            <p><strong>New Status:</strong> {newStatus}</p>
            
            <FormGroup className='form-group mt-3'>
              <FormLabel>Select New Status</FormLabel>
              <Form.Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="">Select Status</option>
                <option value="Upcoming">Upcoming</option>
                <option value="Processing">Processing</option>
                <option value="Shipped">Shipped</option>
                <option value="Delivered">Delivered</option>
              </Form.Select>
            </FormGroup>

            {newStatus && (
              <Alert variant="info" className="mt-3">
                <strong>Status Change:</strong> {orderDetails.giftStatus} → {newStatus}
              </Alert>
            )}

            <div className="d-flex gap-2 justify-content-end mt-3">
              <Button 
                color="secondary" 
                onClick={() => {
                  setShowStatusModal(false);
                  setNewStatus('');
                }}
                disabled={updating}
              >
                Cancel
              </Button>
              <Button 
                color="primary" 
                onClick={handleStatusUpdate}
                disabled={updating || !newStatus}
              >
                {updating ? 'Updating...' : 'Update Status'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </PageWrapper>
  );
};

export default EnhancedOrderDetails;